import { useState } from "react";
import { selectFolderItems } from "../../../../common/Sidebar/redux/selectors";
import { toggleAccordion } from "../../../../common/Sidebar/redux/slice";
import { createStorageObjectApi } from "../../../../services/lib/storageObjectManager";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import type { TransferActions } from "../types";
import { StorageObjectValue } from "../../../../services/api/instances/storageApi/types/service_types";
import { StorageObject } from "../../../../services/api/instances/storageApi/types/object_types";

export const useTransferModal = <T extends StorageObjectValue>(
  objects: StorageObject<T>[],
  actions: TransferActions<T>,
) => {
  const dispatch = useAppDispatch();
  const folderItems = useAppSelector(selectFolderItems);
  const [targetFolder, setTargetFolder] = useState<StorageObject<T>>();

  const storage = createStorageObjectApi<T>(objects);

  const handleSelectChange = (_: any, newValue: StorageObject<T> | null) => {
    const selectedFolder = objects.find((object) => object.id === newValue?.id);
    setTargetFolder(selectedFolder as StorageObject<T>);
  };

  const handleTransfer = (id: string) => {
    const folder: StorageObject<T> | null = storage.findParent(id);
    const itemToTransfer = storage.findById(id);
    const updatedObjects = storage.moveItem(id, targetFolder?.id);
    switch (true) {
      case targetFolder === undefined: {
        if (folder?.children?.length === 1) {
          dispatch(toggleAccordion(folder?.id as string));
        }
        dispatch(
          actions.deleteChild({ childId: id, parentId: folder?.id as string }),
        );
        dispatch(actions.setObjects(updatedObjects));
        dispatch(actions.setSelectedObject(itemToTransfer as StorageObject<T>));
        return;
      }
      case targetFolder !== undefined: {
        if (folder?.children?.length === 1) {
          dispatch(toggleAccordion(folder?.id as string));
        }
        if (!folderItems.includes(targetFolder?.id as string)) {
          dispatch(toggleAccordion(targetFolder?.id as string));
        }
        dispatch(
          actions.updateObjectWithChildren({
            childId: id,
            parentId: targetFolder?.id as string,
          }),
        );
        dispatch(actions.setObjects(updatedObjects));
        dispatch(actions.setSelectedObject(itemToTransfer as StorageObject<T>));
        return;
      }
      default:
        return;
    }
  };

  return {
    targetFolder,
    objects,
    handleTransfer,
    handleSelectChange,
  };
};
